import { zodResolver } from "@hookform/resolvers/zod";
import { XIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, LoadingIndicator, Row } from "@/components";
import { FormRichTextInput } from "@/components/form/FormRichTextInput";
import { FormTextInput } from "@/components/form/FormTextInput";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Sheet, SheetClose, SheetContent } from "@/components/ui/sheet";

import { useCreateCompanyNote } from "@/hooks/mutations/useCreateCompanyNote";
import { useUpdateCompanyNote } from "@/hooks/mutations/useUpdateCompanyNote";
import { useAddCompanyNote } from "@/hooks/sets/useAddCompanyNote";
import { useSetCompanyNote } from "@/hooks/sets/useSetCompanyNote";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CompanyNote } from "@/types";

const companyNoteSchema = z.object({
  content: z.string().min(1, {
    message: "Required",
  }),
  title: z.string().min(1, {
    message: "Required",
  }),
});

const CompanyNoteForm = ({
  companyNote,
  handleSuccess,
}: {
  companyNote: CompanyNote | null;
  handleSuccess: () => void;
}) => {
  const {
    mutate: createCompanyNote,
    isPending: isCreateCompanyNotePending,
    isError: isCreateCompanyNoteError,
  } = useCreateCompanyNote();
  const {
    mutate: updateCompanyNote,
    isPending: isUpdateCompanyNotePending,
    isError: isUpdateCompanyNoteError,
  } = useUpdateCompanyNote();

  const addCompanyNote = useAddCompanyNote();
  const setCompanyNote = useSetCompanyNote();

  const form = useForm<z.infer<typeof companyNoteSchema>>({
    defaultValues: {
      content: companyNote?.content ?? "",
      title: companyNote?.title ?? "",
    },
    resolver: zodResolver(companyNoteSchema),
  });

  const isSavePending =
    isCreateCompanyNotePending || isUpdateCompanyNotePending;
  const isSaveError = isCreateCompanyNoteError || isUpdateCompanyNoteError;

  const handleSubmit = (data: z.infer<typeof companyNoteSchema>) => {
    mixpanelUtils.trackButtonClick(
      companyNote ? "Update note" : "Create note",
      MIXPANEL_SCREENS.COMPANY,
    );
    if (companyNote) {
      updateCompanyNote(
        {
          companyNoteId: companyNote.id,
          updateCompanyNoteRequest: data,
        },
        {
          onSuccess: (updatedCompanyNote) => {
            setCompanyNote({ companyNoteToUpdate: updatedCompanyNote });
            form.reset();
            handleSuccess();
          },
        },
      );
    } else {
      createCompanyNote(
        { createCompanyNoteRequest: data },
        {
          onSuccess: (createdCompanyNote) => {
            addCompanyNote({ companyNoteToAdd: createdCompanyNote });
            form.reset();
            handleSuccess();
          },
        },
      );
    }
  };

  const render = () => {
    return (
      <Form {...form}>
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <Column className="gap-6">
            <FormTextInput
              fieldName="title"
              form={form}
              label="Title"
              placeholder=""
            />
            <FormRichTextInput fieldName="content" form={form} label="Note" />
            <Row className="justify-end">
              <Button
                className="w-24 rounded-full"
                disabled={isSavePending}
                type="submit"
              >
                {isSavePending ? (
                  <LoadingIndicator gearColor="white" variant="small" />
                ) : (
                  <span>Save</span>
                )}
              </Button>
            </Row>
            {isSaveError && (
              <Row className="justify-end">
                <ErrorText
                  errorMessage={
                    companyNote
                      ? "An error occurred while updating note. Please try again."
                      : "An error occurred while creating note. Please try again."
                  }
                />
              </Row>
            )}
          </Column>
        </form>
      </Form>
    );
  };

  return render();
};

export const CompanyNoteSheet = ({
  companyNote,
  onOpenChange,
  open,
}: {
  companyNote: CompanyNote | null;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) => {
  const render = () => {
    return (
      <Sheet
        onOpenChange={(open) => {
          onOpenChange(open);
        }}
        open={open}
      >
        <SheetContent
          // TODO: Better width handling?
          className="w-[70%] !min-w-[1000px] !max-w-[70%] overflow-y-auto p-0"
          hideClose
        >
          <Row className="items-center justify-between px-6 py-4">
            <SheetClose>
              <XIcon className="h-4 w-4 text-slate-700" />
            </SheetClose>
          </Row>
          <Column className="border-t border-slate-200 p-6">
            <CompanyNoteForm
              companyNote={companyNote}
              handleSuccess={() => {
                onOpenChange(false);
              }}
            />
          </Column>
        </SheetContent>
      </Sheet>
    );
  };

  return render();
};
